import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { scenariosDict } from '../support/dictionaries';
import { createPeer, DIRECT_CONNECTION_TIMEOUT_MS, gatherDescription, pairingRequest, readMessage, sendMessage } from './connection';
import { isPlaybackMessage, synchronizeVideo } from './playback';
import { createHttpChannel } from './httpChannel';
import { normalizeRemoteLanguage, remoteErrorKey, remoteText } from './remoteI18n';
import './remote.css';

export default function RemoteViewer() {
    const { hash, search } = useLocation();
    const lang = normalizeRemoteLanguage(new URLSearchParams(search).get('lang'));
    const t = key => remoteText(lang, key);
    const videoRef = useRef(null);
    const screenRef = useRef(null);
    const channelRef = useRef(null);
    const latest = useRef(null);
    const startedRef = useRef(false);
    const [started, setStarted] = useState(false);
    const [status, setStatus] = useState('connecting');
    const [error, setError] = useState('');
    const [nextVideo, setNextVideo] = useState(null);

    const report = useCallback(state => {
        sendMessage(channelRef.current, { type: 'status', state });
    }, []);

    const applyState = useCallback(() => {
        if (!latest.current || !videoRef.current) return;
        const state = latest.current;
        // Metadata/canplay may arrive between heartbeats. Advance the snapshot
        // locally rather than seeking back to its original receipt time.
        const elapsed = state.paused ? 0 : (performance.now() - state.receivedAt) / 1000;
        synchronizeVideo(videoRef.current, {
            ...state, time: state.time + elapsed * state.rate,
        }, startedRef.current, () => {
            if (latest.current !== state) return;
            startedRef.current = false;
            setStarted(false);
            report('blocked');
        });
        if (startedRef.current && videoRef.current.readyState >= 1) {
            // An explicit seek applies once, including when metadata was late.
            state.seek = false;
        }
    }, [report]);

    useEffect(() => {
        const video = videoRef.current;
        latest.current = null;
        startedRef.current = false;
        video?.pause();
        setStarted(false);
        setStatus('connecting');
        setError('');
        setNextVideo(null);
        const abort = new AbortController();
        const rtcAbort = new AbortController();
        let peer;
        let channel;
        let fallback;
        let timeout;
        let disposed = false;
        let failed = false;
        let transport = null;
        let room;
        let token;
        const fail = message => {
            if (disposed || failed) return;
            failed = true;
            clearTimeout(fallback);
            clearTimeout(timeout);
            latest.current = null;
            startedRef.current = false;
            video?.pause();
            channelRef.current = null;
            channel?.close();
            peer?.close();
            setStarted(false);
            setStatus('error');
            setError(message);
        };
        const attach = (nextChannel, method) => {
            channel = nextChannel;
            channelRef.current = channel;
            let opened = false;
            const current = () => !disposed && !failed && channel === nextChannel;
            nextChannel.onopen = () => {
                if (!current() || opened) return;
                opened = true;
                transport = method;
                clearTimeout(fallback);
                clearTimeout(timeout);
                setStatus('connected');
                sendMessage(nextChannel, { type: 'ready' });
            };
            nextChannel.onmessage = event => {
                if (!current()) return;
                const message = readMessage(event);
                if (!isPlaybackMessage(message)) return;
                latest.current = { ...message, receivedAt: performance.now() };
                setNextVideo(message.next);
                applyState();
            };
            nextChannel.onclose = () => {
                if (!current()) return;
                if (!transport) startHttp();
                else fail('viewerDisconnected');
            };
            // Some browsers dispatch datachannel with its state already open.
            if (nextChannel.readyState === 'open') nextChannel.onopen();
        };
        const startHttp = () => {
            if (disposed || failed || transport) return;
            transport = 'http';
            clearTimeout(fallback);
            rtcAbort.abort();
            const previous = channel;
            attach(createHttpChannel({ room: { id: room }, token, role: 'viewer' }), 'http');
            previous?.close();
            if (peer) {
                peer.onconnectionstatechange = null;
                peer.close();
            }
        };
        const join = async () => {
            try {
                const params = new URLSearchParams(hash.slice(1));
                room = params.get('room');
                const invite = params.get('token');
                if (!room || !invite || !/^[\w-]+$/.test(room) || !/^[\w-]+$/.test(invite)) {
                    fail('viewerMissingLink');
                    return;
                }
                const claim = await pairingRequest(`/${room}/claim`, {
                    method: 'POST', token: invite, signal: abort.signal,
                });
                token = claim.viewerToken;
                const offer = await pairingRequest(`/${room}/offer`, { token, signal: abort.signal });
                if (!offer) {
                    fail('viewerLinkNotReady');
                    return;
                }
                if (offer.sdp.includes('a=x-teddy-http-only')) {
                    startHttp();
                    return;
                }
                try {
                    peer = createPeer();
                    peer.ondatachannel = event => {
                        if (disposed || failed || transport === 'http') {
                            event.channel.close();
                            return;
                        }
                        attach(event.channel, 'direct');
                    };
                    peer.onconnectionstatechange = () => {
                        if (disposed || failed || transport === 'http') return;
                        if (['disconnected', 'failed', 'closed'].includes(peer.connectionState)) {
                            if (!transport) startHttp();
                            else fail('viewerLost');
                        }
                    };
                    await peer.setRemoteDescription(offer);
                    await peer.setLocalDescription(await peer.createAnswer());
                    const answer = await gatherDescription(peer, rtcAbort.signal);
                    if (disposed || transport === 'http') return;
                    await pairingRequest(`/${room}/answer`, {
                        method: 'PUT', body: JSON.stringify(answer), token, signal: abort.signal,
                    });
                    if (!disposed && !transport) {
                        fallback = setTimeout(startHttp, DIRECT_CONNECTION_TIMEOUT_MS);
                    }
                } catch (error) {
                    if (!disposed && !abort.signal.aborted) startHttp();
                }
            } catch (error) {
                if (error.name !== 'AbortError') fail(remoteErrorKey(error));
            }
        };
        timeout = setTimeout(() => fail('viewerTimeout'), 30000);
        join();
        return () => {
            disposed = true;
            abort.abort();
            rtcAbort.abort();
            clearTimeout(fallback);
            clearTimeout(timeout);
            latest.current = null;
            startedRef.current = false;
            video?.pause();
            channelRef.current = null;
            channel?.close();
            peer?.close();
        };
    }, [applyState, hash]);

    const fullscreen = () => {
        if (!document.fullscreenElement && screenRef.current?.requestFullscreen) {
            screenRef.current.requestFullscreen().catch(() => {});
        }
    };

    const start = () => {
        fullscreen();
        startedRef.current = true;
        setStarted(true);
        report('ready');
        applyState();
        sendMessage(channelRef.current, { type: 'ready' });
    };

    return (
        <main ref={screenRef} className='remoteViewer' aria-label={t('viewerScreenLabel')} lang={lang} onDoubleClick={fullscreen}>
            <video
                ref={videoRef} className='remoteViewerVideo' muted playsInline preload='auto'
                onLoadedMetadata={applyState} onCanPlay={applyState}
                onPlaying={() => report('playing')} onPause={() => report('paused')}
                onEnded={() => report('paused')} onError={() => report('error')}
                disablePictureInPicture
            />
            {nextVideo && <video className='videoPreload' src={scenariosDict[nextVideo]} preload='auto' muted playsInline aria-hidden='true' tabIndex={-1} />}
            {(!started || status !== 'connected') && (
                <div className='remoteViewerOverlay'>
                    <div className='remoteViewerCard'>
                        <h1>{t('viewerTitle')}</h1>
                        {status === 'connecting' && <p role='status'>{t('viewerConnecting')}</p>}
                        {status === 'error' && <p role='alert'>{t(error)}</p>}
                        {status === 'connected' && <>
                            <p>{t('viewerConnected')}</p>
                            <button type='button' className='btn btn-primary btn-lg' onClick={start}>{t('viewerStart')}</button>
                            <p className='remoteViewerHint'>{t('viewerHint')}</p>
                        </>}
                    </div>
                </div>
            )}
        </main>
    );
}
